import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Tour, type TourProps } from "antd";
import { AdditionsTourProvider as ContextProvider } from "./AdditionsTourContext";
import { useAdditionsTour } from "./useAdditionsTour";
import { useTourContext } from "../tour-context/useTourContext";
import { useResponsive } from "../../hooks/useResponsive";

// ─── Shared style helpers (identical to TourProvider) ────────────────────────
const hiddenButtons = {
    nextButtonProps: { style: { display: "none" } as React.CSSProperties },
    prevButtonProps: { style: { display: "none" } as React.CSSProperties },
};

const STEP_DELAY_MS = 800;

const S: React.CSSProperties = { fontSize: 14, lineHeight: 1.5, padding: 12 };
const T = (text: React.ReactNode) => <div style={S}>{text}</div>;

// ─── Step index tables ────────────────────────────────────────────────────────
// create-additions
// Desktop: 0=WELCOME 1=ORDERS_NAV 2=MENU_NAV 3=ADDITIONS_TAB 4=ADD_GROUP 5=HIDDEN_GROUP 6=ADD_ADDITION 7=HIDDEN_ADD 8=DONE
// Mobile:  0=WELCOME 1=HAMBURGER  2=ORDERS_NAV 3=MENU_NAV 4=ADDITIONS_TAB 5=ADD_GROUP 6=HIDDEN_GROUP 7=ADD_ADDITION 8=HIDDEN_ADD 9=DONE
//
// attach-additions
// Desktop: 0=WELCOME 1=ORDERS_NAV 2=MENU_NAV 3=INTRO 4=EXT_CONFIG 5=SAVE
// Mobile:  0=WELCOME 1=HAMBURGER  2=ORDERS_NAV 3=MENU_NAV 4=INTRO 5=EXT_CONFIG 6=SAVE

// ─── Inner Tour component ─────────────────────────────────────────────────────
const AdditionsTourInner: React.FC = () => {
    const { t } = useTranslation();
    const {
        activeTour,
        tourStep,
        isTourOpen,
        closeTour,
        setTourStep,
        additionsTabRef,
        addGroupBtnRef,
        addAdditionBtnRef,
        editFirstProductRef,
        firstExtensionCategoryRef,
    } = useAdditionsTour();

    // Borrow shared nav refs from the existing TourContext
    const {
        mobileMenuBtnRef,
        mobileOrdersMenuRef,
        ordersNavRef,
        menuNavRef,
    } = useTourContext();

    const { isMobile } = useResponsive();

    const [visibleStep, setVisibleStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const justOpenedRef = useRef(false);

    useEffect(() => {
        if (isTourOpen) {
            justOpenedRef.current = true;
            setVisibleStep(tourStep);
            setIsVisible(true);
        } else {
            if (timerRef.current) clearTimeout(timerRef.current);
            setIsVisible(false);
        }
    }, [isTourOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isTourOpen) return;
        if (justOpenedRef.current) {
            justOpenedRef.current = false;
            return;
        }
        if (timerRef.current) clearTimeout(timerRef.current);
        setIsVisible(false);
        timerRef.current = setTimeout(() => {
            setVisibleStep(tourStep);
            setIsVisible(true);
        }, STEP_DELAY_MS);
        return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    }, [tourStep, isTourOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── TOUR 1: Tworzenie dodatków ────────────────────────────────────────────
    const buildCreateAdditionsSteps = (): TourProps["steps"] => {
        const steps: TourProps["steps"] = [];

        // [0] WELCOME
        steps.push({
            title: null,
            description: T(<>
                ➕ <strong>{t("additionsTourContext.createWelcomeTitle")}</strong>
                <br /><br />
                {t("additionsTourContext.createWelcomeIntro")}
                <br /><br />
                {t("additionsTourContext.createWelcomeBodyBefore")} <strong>{t("additionsTourContext.additionsGroup")}</strong> {t("additionsTourContext.createWelcomeBodyMiddle")} <strong>{t("additionsTourContext.letsStart")}</strong>!
            </>),
            target: null,
            nextButtonProps: { children: t("additionsTourContext.letsStart") },
            prevButtonProps: { style: { display: "none" } },
        });

        if (isMobile) {
            // [Mobile 1] HAMBURGER
            steps.push({
                title: null,
                description: T(<>
                    {t("additionsTourContext.hamburgerHintBefore")}{" "}
                    <strong>{t("additionsTourContext.navigationMenu")}</strong>.
                </>),
                target: () => mobileMenuBtnRef.current as HTMLElement,
                placement: "bottom",
                ...hiddenButtons,
            });
        }

        // [Desktop 1 / Mobile 2] ORDERS_NAV – click the bell icon
        steps.push({
            title: null,
            description: T(<>
                🔔{" "}
                {isMobile
                    ? t("additionsTourContext.bellHintMobile")
                    : t("additionsTourContext.bellHintDesktop")}{" "}
                {t("additionsTourContext.bellClickBefore")} <strong>{t("additionsTourContext.ordersSection")}</strong>.
            </>),
            target: () => {
                if (isMobile) {
                    const el = mobileOrdersMenuRef.current?.querySelector('li[data-menu-id="orders"]');
                    return (el ?? mobileOrdersMenuRef.current) as HTMLElement;
                }
                return ordersNavRef.current as HTMLElement;
            },
            placement: isMobile ? "bottom" : "right",
            ...hiddenButtons,
        });

        // [Desktop 2 / Mobile 3] MENU_NAV – click "Menu produktów" button in OrdersHeader
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.menuNavBefore")} <strong>{t("additionsTourContext.productMenu")}</strong>
                {isMobile ? t("additionsTourContext.atTopOfScreen") : t("additionsTourContext.atTopRightCorner")}?{" "}
                <strong>{t("additionsTourContext.clickIt")}</strong> {t("additionsTourContext.menuNavAfterCreate")}
            </>),
            target: () => menuNavRef.current as HTMLElement,
            placement: "bottom",
            ...hiddenButtons,
        });

        // [Desktop 3 / Mobile 4] ADDITIONS_TAB
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.additionsTabBefore")} <strong>{t("additionsTourContext.additionsTabLabel")}</strong>{" "}
                {t("additionsTourContext.additionsTabAfter")}
            </>),
            target: () => additionsTabRef.current as HTMLElement,
            placement: "bottom",
            ...hiddenButtons,
        });

        // [Desktop 4 / Mobile 5] ADD_GROUP_BTN
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.addGroupIntro")}{" "}
                {t("additionsTourContext.clickWord")} <strong>{t("additionsTourContext.addGroupButton")}</strong>{t("additionsTourContext.addGroupMiddle")} <strong>{t("additionsTourContext.createCategory")}</strong>.
            </>),
            target: () => addGroupBtnRef.current as HTMLElement,
            placement: isMobile ? "bottom" : "bottomLeft",
            ...hiddenButtons,
        });

        // [Desktop 5 / Mobile 6] HIDDEN (modal open)
        steps.push({
            title: null,
            description: <></>,
            target: null,
            className: "hidden-tour-step",
            mask: false,
        });

        // [Desktop 6 / Mobile 7] ADD_ADDITION_BTN
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.addAdditionReady")}{" "}
                {t("additionsTourContext.clickWord")} <strong>{t("additionsTourContext.addAdditionButton")}</strong> {t("additionsTourContext.addAdditionMiddle1")} <strong>{t("additionsTourContext.nameAndPrice")}</strong>{t("additionsTourContext.addAdditionMiddle2")} <strong>{t("additionsTourContext.createProduct")}</strong>.
            </>),
            target: () => addAdditionBtnRef.current as HTMLElement,
            placement: isMobile ? "bottom" : "bottomLeft",
            ...hiddenButtons,
        });

        // [Desktop 7 / Mobile 8] HIDDEN (modal open)
        steps.push({
            title: null,
            description: <></>,
            target: null,
            className: "hidden-tour-step",
            mask: false,
        });

        // [Desktop 8 / Mobile 9] DONE
        steps.push({
            title: null,
            description: T(<>
                🎉 <strong>{t("additionsTourContext.bravo")}</strong> {t("additionsTourContext.createDoneIntro")}
                <br /><br />
                {t("additionsTourContext.createDoneBodyBefore")}{" "}
                <strong>{t("additionsTourContext.createDoneBodyHighlight")}</strong>{" "}
                {t("additionsTourContext.createDoneBodyAfter")}
            </>),
            target: null,
            nextButtonProps: { children: t("additionsTourContext.finish"), onClick: closeTour },
            prevButtonProps: { style: { display: "none" } },
        });

        return steps;
    };

    // ── TOUR 2: Podpinanie dodatków do produktu ───────────────────────────────
    const buildAttachAdditionsSteps = (): TourProps["steps"] => {
        const steps: TourProps["steps"] = [];

        // [0] WELCOME
        steps.push({
            title: null,
            description: T(<>
                🔗 <strong>{t("additionsTourContext.attachWelcomeTitle")}</strong>
                <br /><br />
                {t("additionsTourContext.attachWelcomeIntro")}
                <br /><br />
                {t("additionsTourContext.clickWord")} <strong>{t("additionsTourContext.letsStart")}</strong>!
            </>),
            target: null,
            nextButtonProps: { children: t("additionsTourContext.letsStart") },
            prevButtonProps: { style: { display: "none" } },
        });

        if (isMobile) {
            // [Mobile 1] HAMBURGER
            steps.push({
                title: null,
                description: T(<>
                    {t("additionsTourContext.attachHamburgerHintBefore")} <strong>{t("additionsTourContext.navigationMenu")}</strong>.
                </>),
                target: () => mobileMenuBtnRef.current as HTMLElement,
                placement: "bottom",
                ...hiddenButtons,
            });
        }

        // [Desktop 1 / Mobile 2] ORDERS_NAV
        steps.push({
            title: null,
            description: T(<>
                🔔{" "}
                {isMobile
                    ? t("additionsTourContext.bellHintMobile")
                    : t("additionsTourContext.bellHintDesktop")}{" "}
                {t("additionsTourContext.bellClickBefore")} <strong>{t("additionsTourContext.ordersSection")}</strong>.
            </>),
            target: () => {
                if (isMobile) {
                    const el = mobileOrdersMenuRef.current?.querySelector('li[data-menu-id="orders"]');
                    return (el ?? mobileOrdersMenuRef.current) as HTMLElement;
                }
                return ordersNavRef.current as HTMLElement;
            },
            placement: isMobile ? "bottom" : "right",
            ...hiddenButtons,
        });

        // [Desktop 2 / Mobile 3] MENU_NAV
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.menuNavBefore")} <strong>{t("additionsTourContext.productMenu")}</strong>
                {isMobile ? t("additionsTourContext.atTopOfScreen") : t("additionsTourContext.atTopRightCorner")}?{" "}
                <strong>{t("additionsTourContext.clickIt")}</strong> {t("additionsTourContext.menuNavAfterAttach")}
            </>),
            target: () => menuNavRef.current as HTMLElement,
            placement: "bottom",
            ...hiddenButtons,
        });

        // [Desktop 3 / Mobile 4] EDIT_PRODUCT – highlight first product row, user must click it
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.editProductBefore")} <strong>{t("additionsTourContext.firstProductOnList")}</strong>{" "}
                {t("additionsTourContext.editProductAfter")}
            </>),
            target: () => editFirstProductRef.current as HTMLElement,
            placement: "bottom",
            ...hiddenButtons,
        });

        // [Desktop 4 / Mobile 5] FIRST_EXTENSION_CATEGORY – highlight first extension group, user must check it
        steps.push({
            title: null,
            description: T(<>
                {t("additionsTourContext.extCategoryBefore")} <strong>{t("additionsTourContext.additionsTabLabel")}</strong> {t("additionsTourContext.extCategoryMiddle")}{" "}
                {t("additionsTourContext.clickWord")} <strong>{t("additionsTourContext.firstGroupOnList")}</strong> {t("additionsTourContext.extCategoryAfter")}
            </>),
            target: () => firstExtensionCategoryRef.current as HTMLElement,
            placement: "top",
            ...hiddenButtons,
        });

        // [Desktop 5 / Mobile 6] SAVE
        steps.push({
            title: null,
            description: T(<>
                🎉 {t("additionsTourContext.saveStepBefore")} <strong>{t("additionsTourContext.saveButton")}</strong>{" "}
                {t("additionsTourContext.saveStepAfter")}
                <br /><br />
                {t("additionsTourContext.moreGuidesBefore")} <strong>{t("additionsTourContext.tutorialsHighlight")}</strong>.
            </>),
            target: null,
            nextButtonProps: { children: t("additionsTourContext.finish"), onClick: closeTour },
            prevButtonProps: { style: { display: "none" } },
        });

        return steps;
    };

    const steps = activeTour === "create-additions"
        ? buildCreateAdditionsSteps()
        : buildAttachAdditionsSteps();

    // Hidden step detection (modals open) – only in create-additions
    const hiddenStepIndexes = activeTour === "create-additions"
        ? [isMobile ? 6 : 5, isMobile ? 8 : 7]
        : [];

    const isHiddenStep = hiddenStepIndexes.includes(tourStep);

    if (!isTourOpen || !activeTour) return null;

    return (
        <Tour
            open={isVisible && !isHiddenStep}
            onClose={closeTour}
            steps={steps!}
            current={visibleStep}
            onChange={(step) => setTourStep(step)}
            scrollIntoViewOptions={isMobile ? false : { behavior: "smooth", block: "center" }}
            mask={{ style: { boxShadow: "inset 0 0 15px #333" } }}
            type="default"
            zIndex={99999}
        />
    );
};

// ─── Public provider ──────────────────────────────────────────────────────────
export const AdditionsTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <ContextProvider>
            {children}
            <AdditionsTourInner />
        </ContextProvider>
    );
};
